import React, { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import {
	Card,
	Form,
	Container,
	Button,
	Alert,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';

export default function PortalForgotPassword() {
	const emailRef = useRef();
	const { resetPassword } = useAuth();
	const [error, setError] = useState('');
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);

	// One string, shown whether or not the address has an account — see the note
	// in the catch block. Worded so a parent who mistypes their email is not left
	// waiting on mail that was never sent.
	const SENT_MESSAGE =
		"If an account exists for that email address, we've sent a password reset link. Check your inbox — and your spam folder — for instructions.";

	async function handleSubmit(e) {
		e.preventDefault();

		try {
			setMessage('');
			setError('');
			setLoading(true);
			await resetPassword(emailRef.current.value);
			setMessage(SENT_MESSAGE);
		} catch (error) {
			console.error('Password reset error:', error);
			// Never confirm or deny that an address has an account — that answer is
			// exactly what email enumeration protection exists to withhold, and this
			// page used to print it in plain English. Report the same thing we report
			// on success. Deliberately NOT relying on the Firebase console setting
			// (enabled 2026-08-02) to suppress this: if it is ever turned back off,
			// the leak must not silently return here.
			if (error.code === 'auth/user-not-found') {
				setMessage(SENT_MESSAGE);
			} else if (error.code === 'auth/invalid-email') {
				setError('Please enter a valid email address.');
			} else if (error.code === 'auth/too-many-requests') {
				setError('Too many requests. Please try again later.');
			} else {
				setError('Failed to send password reset email. Please try again.');
			}
		}

		setLoading(false);
	}

	return (
		<Container className='justify-content-center py-5' fluid>
			<Card
				className='mx-auto bg-transparent border-1 p-2 shadow-sm'
				style={{ maxWidth: '450px' }}>
				<Card.Header className='bg-transparent border-0 text-center'>
					<h2 className='mb-0' style={{ color: '#384971' }}>
						Reset Password
					</h2>
					<p className='text-muted small mt-2'>
						Enter your email to receive a password reset link
					</p>
				</Card.Header>
				<Card.Body>
					{error && <Alert variant='danger'>{error}</Alert>}
					{message && <Alert variant='success'>{message}</Alert>}
					<Form onSubmit={handleSubmit}>
						<Form.Group className='mb-4'>
							<Form.Label>Email Address</Form.Label>
							<Form.Control
								type='email'
								placeholder='Enter your email'
								ref={emailRef}
								required
								autoComplete='email'
							/>
							<Form.Text className='text-muted'>
								We'll send you a link to reset your password.
							</Form.Text>
						</Form.Group>
						<div className='d-grid gap-2'>
							<Button
								disabled={loading}
								className='shadow-none'
								variant='primary'
								type='submit'
								size='lg'>
								{loading ? 'Sending...' : 'Send Reset Link'}
							</Button>
						</div>
					</Form>
				</Card.Body>
				<Card.Footer className='bg-transparent border-0 text-center'>
					<p className='mb-2'>
						Remember your password?{' '}
						<Link to='/portal/login'>Back to Login</Link>
					</p>
					<p className='text-muted small mb-0'>
						Don't have an account?{' '}
						<Link to='/portal/signup'>Sign Up</Link>
					</p>
				</Card.Footer>
			</Card>
		</Container>
	);
}
